import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTenant } from '@/contexts/TenantContext';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Zap,
  Plus,
  FileText,
  ChevronLeft,
  ChevronRight,
  Folder,
  ChevronRight as ChevronRightIcon,
  Search
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { AutomationTemplates } from '@/components/automation/AutomationTemplates';
import { AutomationCardCompact } from '@/components/automation/AutomationCardCompact';
import { FolderTree } from '@/components/automation/FolderTree';
import { CreateFolderDialog } from '@/components/automation/CreateFolderDialog';
import { MoveFolderDialog } from '@/components/automation/MoveFolderDialog';
import { type AutomationTemplate } from '@/data/automationTemplates';
import { useAuth } from '@/contexts/AuthContext';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import type { Automation, AutomationFolder } from '@/types/automation';

const ITEMS_PER_PAGE_KEY = 'automation-items-per-page';

export function AutomationManagement() {
  // State
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [folders, setFolders] = useState<AutomationFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [editingFolder, setEditingFolder] = useState<AutomationFolder | null>(null);
  const [movingAutomation, setMovingAutomation] = useState<Automation | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [draggedAutomationId, setDraggedAutomationId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem(ITEMS_PER_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 20;
  });

  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTenant } = useTenant();

  // Load data on mount and tenant change
  useEffect(() => {
    loadAutomations();
    loadFolders();
  }, [currentTenant?.tenant_id]);

  // Save items per page to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(ITEMS_PER_PAGE_KEY, itemsPerPage.toString());
  }, [itemsPerPage]);

  // ============================================================================
  // Data Loading
  // ============================================================================

  const loadAutomations = async () => {
    try {
      setLoading(true);

      const tenantId = await getCurrentTenantId();
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      const { data, error } = await supabase
        .from('automations')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading automations:', error);
        toast.error('Failed to load automations');
        return;
      }

      setAutomations(data || []);
    } catch (error) {
      console.error('Error loading automations:', error);
      toast.error('Failed to load automations');
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    try {
      const tenantId = await getCurrentTenantId();
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      const { data, error } = await supabase
        .from('automation_folders')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error loading folders:', error);
        toast.error('Failed to load folders');
        return;
      }

      // Calculate automation count for each folder
      const foldersWithCounts = (data || []).map(folder => ({
        ...folder,
        automation_count: automations.filter(a => a.folder_id === folder.id).length
      }));

      setFolders(foldersWithCounts);
    } catch (error) {
      console.error('Error loading folders:', error);
      toast.error('Failed to load folders');
    }
  };

  // Recalculate folder counts when automations change
  useEffect(() => {
    if (folders.length > 0) {
      setFolders(prev =>
        prev.map(folder => ({
          ...folder,
          automation_count: automations.filter(a => a.folder_id === folder.id).length
        }))
      );
    }
  }, [automations]);

  // ============================================================================
  // Automation Operations
  // ============================================================================

  const handleToggleAutomation = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('automations')
        .update({ is_active: !isActive })
        .eq('id', id);

      if (error) throw error;

      setAutomations(prev =>
        prev.map(automation =>
          automation.id === id
            ? { ...automation, is_active: !isActive }
            : automation
        )
      );

      toast.success(`Automation ${!isActive ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Error toggling automation:', error);
      toast.error('Failed to toggle automation');
    }
  };

  const handleDeleteAutomation = async (id: string) => {
    if (!confirm('Are you sure you want to delete this automation?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('automations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setAutomations(prev => prev.filter(automation => automation.id !== id));
      toast.success('Automation deleted successfully');
    } catch (error) {
      console.error('Error deleting automation:', error);
      toast.error('Failed to delete automation');
    }
  };

  const handleCreateFromTemplate = async (template: AutomationTemplate, name: string) => {
    try {
      if (!user) {
        toast.error('You must be logged in to create automations');
        return;
      }

      const automationData = {
        name,
        description: template.description,
        is_active: true,
        steps: template.steps.map((step, index) => ({
          ...step,
          id: `${step.type}-${Date.now()}-${index}`,
          position: index
        })),
        created_by: user.id,
        tenant_id: await getCurrentTenantId(),
        folder_id: selectedFolderId // Auto-assign to current folder if viewing folder
      };

      const { data, error } = await supabase
        .from('automations')
        .insert(automationData)
        .select()
        .single();

      if (error) throw error;

      await loadAutomations();
      toast.success('Automation created from template successfully');
      setShowTemplates(false);

      // Navigate to edit the new automation
      navigate(`/app/automation-builder/${data.id}`);
    } catch (error) {
      console.error('Error creating automation from template:', error);
      toast.error('Failed to create automation from template');
    }
  };

  // ============================================================================
  // Folder Operations
  // ============================================================================

  const handleCreateFolder = async (data: {
    name: string;
    description?: string;
    color?: string;
  }) => {
    try {
      if (!user) {
        toast.error('You must be logged in to create folders');
        return;
      }

      const tenantId = await getCurrentTenantId();
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      const folderData = {
        name: data.name,
        description: data.description,
        color: data.color,
        tenant_id: tenantId,
        created_by: user.id,
        display_order: folders.length // Add to end
      };

      const { error } = await supabase
        .from('automation_folders')
        .insert(folderData);

      if (error) throw error;

      await loadFolders();
      toast.success('Folder created successfully');
      setShowFolderDialog(false);
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
    }
  };

  const handleRenameFolder = (folderId: string) => {
    const folder = folders.find(f => f.id === folderId);
    if (folder) {
      setEditingFolder(folder);
      setShowFolderDialog(true);
    }
  };

  const handleUpdateFolder = async (data: {
    name: string;
    description?: string;
    color?: string;
  }) => {
    try {
      if (!editingFolder) return;

      const { error } = await supabase
        .from('automation_folders')
        .update({
          name: data.name,
          description: data.description,
          color: data.color
        })
        .eq('id', editingFolder.id);

      if (error) throw error;

      await loadFolders();
      toast.success('Folder updated successfully');
      setShowFolderDialog(false);
      setEditingFolder(null);
    } catch (error) {
      console.error('Error updating folder:', error);
      toast.error('Failed to update folder');
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    const folder = folders.find(f => f.id === folderId);
    if (!folder) return;

    if (folder.automation_count && folder.automation_count > 0) {
      toast.error('Cannot delete folder that contains automations');
      return;
    }

    if (!confirm(`Are you sure you want to delete the folder "${folder.name}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('automation_folders')
        .delete()
        .eq('id', folderId);

      if (error) throw error;

      await loadFolders();
      toast.success('Folder deleted successfully');

      // If we were viewing this folder, reset to uncategorized
      if (selectedFolderId === folderId) {
        setSelectedFolderId(null);
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast.error('Failed to delete folder');
    }
  };

  // ============================================================================
  // Move Automation to Folder
  // ============================================================================

  const handleInitiateMove = (automationId: string) => {
    const automation = automations.find(a => a.id === automationId);
    if (automation) {
      setMovingAutomation(automation);
      setShowMoveDialog(true);
    }
  };

  const handleMoveToFolder = async (targetFolderId: string | null) => {
    if (!movingAutomation?.id) return;

    try {
      const { error } = await supabase
        .from('automations')
        .update({ folder_id: targetFolderId })
        .eq('id', movingAutomation.id);

      if (error) throw error;

      // Update local state
      setAutomations(prev =>
        prev.map(automation =>
          automation.id === movingAutomation.id
            ? { ...automation, folder_id: targetFolderId || undefined }
            : automation
        )
      );

      const folderName = targetFolderId
        ? folders.find(f => f.id === targetFolderId)?.name
        : 'Automations';

      toast.success(`Moved to ${folderName}`);
    } catch (error) {
      console.error('Error moving automation:', error);
      toast.error('Failed to move automation');
    } finally {
      setMovingAutomation(null);
      setShowMoveDialog(false);
    }
  };

  // ============================================================================
  // Drag and Drop
  // ============================================================================

  const handleDragStart = (e: React.DragEvent, automationId: string) => {
    setDraggedAutomationId(automationId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedAutomationId(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetFolderId: string | null) => {
    e.preventDefault();

    if (!draggedAutomationId) return;

    try {
      const { error } = await supabase
        .from('automations')
        .update({ folder_id: targetFolderId })
        .eq('id', draggedAutomationId);

      if (error) throw error;

      // Update local state
      setAutomations(prev =>
        prev.map(automation =>
          automation.id === draggedAutomationId
            ? { ...automation, folder_id: targetFolderId || undefined }
            : automation
        )
      );

      const folderName = targetFolderId
        ? folders.find(f => f.id === targetFolderId)?.name
        : 'Automations';

      toast.success(`Moved to ${folderName}`);
    } catch (error) {
      console.error('Error moving automation:', error);
      toast.error('Failed to move automation');
    } finally {
      setDraggedAutomationId(null);
    }
  };

  // ============================================================================
  // Filtering and Pagination
  // ============================================================================

  // Filter automations by selected folder and search query
  const filteredAutomations = useMemo(() => {
    let filtered = automations;

    // If searching, show all matching automations across all folders
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        a.name?.toLowerCase().includes(query) ||
        a.description?.toLowerCase().includes(query)
      );
    } else {
      // No search: Filter by folder
      if (selectedFolderId === null) {
        // Root level: Show only uncategorized automations (folders will be shown separately)
        filtered = filtered.filter(a => !a.folder_id);
      } else {
        // Folder view: Show automations in selected folder
        filtered = filtered.filter(a => a.folder_id === selectedFolderId);
      }
    }

    return filtered;
  }, [automations, selectedFolderId, searchQuery]);

  // Determine if we're at root level (showing folders + uncategorized)
  const isRootLevel = selectedFolderId === null;

  // When searching, don't show folders
  const isSearching = searchQuery.trim().length > 0;

  // Pagination calculations
  const totalItems = filteredAutomations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAutomations = filteredAutomations.slice(startIndex, endIndex);

  // Reset to page 1 when folder, search, or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFolderId, searchQuery, itemsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleItemsPerPageChange = (value: string) => {
    const newValue = value === 'all' ? totalItems : parseInt(value, 10);
    setItemsPerPage(newValue);
  };

  // ============================================================================
  // Helper Functions
  // ============================================================================

  const uncategorizedCount = automations.filter(a => !a.folder_id).length;

  // ============================================================================
  // Render
  // ============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex gap-0 h-[calc(100vh-200px)]">
      {/* Folder Sidebar */}
      <FolderTree
        folders={folders}
        selectedFolderId={selectedFolderId}
        onSelectFolder={setSelectedFolderId}
        onCreateFolder={() => {
          setEditingFolder(null);
          setShowFolderDialog(true);
        }}
        onRenameFolder={handleRenameFolder}
        onDeleteFolder={handleDeleteFolder}
        uncategorizedCount={uncategorizedCount}
        automations={automations}
        onEditAutomation={(id) => navigate(`/app/automation-builder/${id}`)}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col p-6 overflow-auto">
        {/* Header */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">
                {selectedFolderId
                  ? folders.find(f => f.id === selectedFolderId)?.name
                  : 'Automations'}
              </h2>
              <p className="text-muted-foreground text-sm">
                {isSearching
                  ? `${totalItems} automation${totalItems !== 1 ? 's' : ''} found`
                  : isRootLevel
                  ? `${folders.length} folder${folders.length !== 1 ? 's' : ''}, ${totalItems} uncategorized automation${totalItems !== 1 ? 's' : ''}`
                  : `${totalItems} automation${totalItems !== 1 ? 's' : ''}`}
              </p>
            </div>

            <div className="flex items-center gap-2">
              {/* Template Button */}
              <Button
                variant="outline"
                onClick={() => setShowTemplates(true)}
                className="flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Use Template
              </Button>

              {/* Create Button */}
              <Button
                onClick={() => navigate('/app/automation-builder/new')}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Automation
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search automations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Empty State - Only show when truly empty (no folders AND no automations) */}
        {!isRootLevel && filteredAutomations.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Zap className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">
                No Automations in This Folder
              </h3>
              <p className="text-muted-foreground mb-4">
                Drag automations here or create a new one to get started.
              </p>
              <div className="flex gap-2 justify-center">
                <Button
                  variant="outline"
                  onClick={() => setShowTemplates(true)}
                  className="flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Start with Template
                </Button>
                <Button
                  onClick={() => navigate('/app/automation-builder/new')}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create from Scratch
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : isRootLevel && folders.length === 0 && filteredAutomations.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Zap className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">
                No Automations Yet
              </h3>
              <p className="text-muted-foreground mb-4">
                Create your first automation to streamline your workflow.
              </p>
              <div className="flex gap-2 justify-center">
                <Button
                  variant="outline"
                  onClick={() => setShowTemplates(true)}
                  className="flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Start with Template
                </Button>
                <Button
                  onClick={() => navigate('/app/automation-builder/new')}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create from Scratch
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex-1 space-y-4">
              {/* Folder List (only at root level and not searching) */}
              {isRootLevel && !isSearching && folders.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-2 px-1">
                    Folders
                  </h3>
                  <div className="space-y-1.5 mb-6">
                    {folders.map((folder) => (
                      <div
                        key={folder.id}
                        className="group h-[52px] border rounded-md px-3 py-1.5 flex items-center justify-between gap-2 transition-all hover:border-primary/50 hover:shadow-sm bg-card cursor-pointer"
                        onClick={() => setSelectedFolderId(folder.id)}
                      >
                        {/* Left: Icon + Name + Count */}
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {/* Folder Icon */}
                          <div className="flex-shrink-0">
                            <Folder
                              className="w-4 h-4"
                              style={{ color: folder.color || 'hsl(var(--muted-foreground))' }}
                            />
                          </div>

                          {/* Folder Name */}
                          <h4 className="font-medium text-sm truncate flex-1 min-w-0" title={folder.name}>
                            {folder.name}
                          </h4>

                          {/* Automation Count */}
                          <div className="flex-shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                            {folder.automation_count || 0} automation{folder.automation_count !== 1 ? 's' : ''}
                          </div>
                        </div>

                        {/* Right: Arrow Icon */}
                        <ChevronRightIcon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Automation List */}
              {filteredAutomations.length > 0 && (
                <div>
                  {(isRootLevel && !isSearching) && (
                    <h3 className="text-sm font-semibold text-muted-foreground mb-2 px-1">
                      Uncategorized Automations
                    </h3>
                  )}
                  {isSearching && (
                    <h3 className="text-sm font-semibold text-muted-foreground mb-2 px-1">
                      Search Results
                    </h3>
                  )}
                  <div className="space-y-1.5">
                    {paginatedAutomations.map((automation) => {
                      // Find folder info when searching
                      const folder = isSearching && automation.folder_id
                        ? folders.find(f => f.id === automation.folder_id)
                        : undefined;

                      return (
                        <AutomationCardCompact
                          key={automation.id}
                          automation={automation}
                          onEdit={(id) => navigate(`/app/automation-builder/${id}`)}
                          onToggle={handleToggleAutomation}
                          onDelete={handleDeleteAutomation}
                          onMove={handleInitiateMove}
                          draggable
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          folderName={folder?.name}
                          folderColor={folder?.color}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t pt-4">
                <div className="text-sm text-muted-foreground">
                  Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} automations
                </div>

                <div className="flex items-center gap-2">
                  {/* Items per Page */}
                  <span className="text-sm text-muted-foreground">Items:</span>
                  <Select
                    value={itemsPerPage === totalItems ? 'all' : itemsPerPage.toString()}
                    onValueChange={handleItemsPerPageChange}
                  >
                    <SelectTrigger className="w-20 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Page Navigation */}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>

                    <span className="text-sm px-3">
                      Page {currentPage} of {totalPages}
                    </span>

                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Dialogs */}
      <AutomationTemplates
        isOpen={showTemplates}
        onClose={() => setShowTemplates(false)}
        onSelectTemplate={handleCreateFromTemplate}
      />

      <CreateFolderDialog
        open={showFolderDialog}
        onOpenChange={(open) => {
          setShowFolderDialog(open);
          if (!open) {
            setEditingFolder(null);
          }
        }}
        onSubmit={editingFolder ? handleUpdateFolder : handleCreateFolder}
        editingFolder={editingFolder}
      />

      <MoveFolderDialog
        open={showMoveDialog}
        onOpenChange={setShowMoveDialog}
        folders={folders}
        currentFolderId={movingAutomation?.folder_id}
        onMove={handleMoveToFolder}
        automationName={movingAutomation?.name || ''}
      />
    </div>
  );
}
